import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { User, UpdateProfileData } from '@/types/index';
import { Input } from '@components/common/Input';
import { Button } from '@components/common/Button';
import { authService } from '@services/auth-service';

interface ProfileInfoFormProps {
  user: User;
  onSuccess: () => void;
}

export const ProfileInfoForm: FC<ProfileInfoFormProps> = ({ user, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateProfileData>({
    defaultValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      buyerAddress: user.buyerAddress || '',
      birthDate: user.birthDate ? user.birthDate.substring(0, 10) : '',
      birthPlace: user.birthPlace || '',
    },
  });

  const onSubmit = async (data: UpdateProfileData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      await authService.updateProfile(data);

      setSuccess('Profil mis à jour avec succès');
      setIsEditing(false);
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (err) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      setError('Erreur lors de la mise à jour du profil. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      buyerAddress: user.buyerAddress || '',
      birthDate: user.birthDate ? user.birthDate.substring(0, 10) : '',
      birthPlace: user.birthPlace || '',
    });
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  if (!isEditing) {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Prénom
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {user.firstName || '—'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Nom
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {user.lastName || '—'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Email
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user.email}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Téléphone
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {user.phone || '—'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Adresse
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {user.buyerAddress || '—'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Date de naissance
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {user.birthDate
              ? new Date(user.birthDate).toLocaleDateString('fr-CH')
              : '—'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Lieu de naissance
          </label>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
            {user.birthPlace || '—'}
          </p>
        </div>

        <Button onClick={() => setIsEditing(true)} variant="primary">
          Modifier
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
          {success}
        </div>
      )}

      <Input
        label="Prénom"
        {...register('firstName', {
          required: 'Le prénom est requis',
          minLength: {
            value: 2,
            message: 'Le prénom doit contenir au moins 2 caractères',
          },
        })}
        error={errors.firstName?.message}
        disabled={isSubmitting}
      />

      <Input
        label="Nom"
        {...register('lastName', {
          required: 'Le nom est requis',
          minLength: {
            value: 2,
            message: 'Le nom doit contenir au moins 2 caractères',
          },
        })}
        error={errors.lastName?.message}
        disabled={isSubmitting}
      />

      <Input
        label="Email"
        type="email"
        {...register('email', {
          required: 'L\'email est requis',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Email invalide',
          },
        })}
        error={errors.email?.message}
        disabled={isSubmitting}
      />

      <Input
        label="Téléphone (optionnel)"
        type="tel"
        {...register('phone')}
        disabled={isSubmitting}
      />

      <Input
        label="Adresse (optionnel)"
        {...register('buyerAddress')}
        disabled={isSubmitting}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Date de naissance (optionnel)"
          type="date"
          {...register('birthDate')}
          disabled={isSubmitting}
        />
        <Input
          label="Lieu de naissance (optionnel)"
          {...register('birthPlace')}
          placeholder="Morges, VD"
          disabled={isSubmitting}
        />
      </div>

      <div className="flex gap-3">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
        <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Annuler
        </Button>
      </div>
    </form>
  );
};
